import React, { useState, useEffect } from "react";
import { Container, Grid, Typography, InputLabel, FormControl, Select, MenuItem, Button, Link, FormControlLabel, Switch } from "@material-ui/core";
import { GridTextField } from "components";
import { useSnackbar } from "notistack";
import { Add as AddIcon, Close as CloseIcon } from "@material-ui/icons";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { CheckInInfo, getCheckInsSingleClub, patchCheckInsEnd, postCheckIns } from "api/checkins";
import { ContactData } from "api/user";
import { PersistedData, getPersistedData, setPersistedData } from "utils/persist";
import i18n from "./i18n";

// prop types
interface GuestCheckinViewProps {
    urlData: { [x: string]: string },
}

// register club form view
const GuestCheckinView = ({ urlData }: GuestCheckinViewProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [checkinInfo, setCheckinInfo] = useState<CheckInInfo>();
    const [city, setCity] = useState("");
    const [companionsContactData, setCompanionsContactData] = useState<ContactData[]>([]);
    const [durationMins, setDurationMins] = useState(60);
    const [fullName, setFullName] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [notSameContactData, setNotSameContactData] = useState<number[]>([]);
    const [postalCode, setPostalCode] = useState("");
    const [showCheckedOutSuccess, setShowCheckedOutSuccess] = useState(false);
    const [showInvalidFields, setShowInvalidFields] = useState(false);
    const [street, setStreet] = useState("");
    const [telephoneCountryCode, setTelephoneCountryCode] = useState("+49");
    const [telephoneNumber, setTelephoneNumber] = useState("");
    const isValidLink = urlData.clubId && urlData.latitude && urlData.longitude;

    // validate data fields
    const isValidValue = (field: keyof ContactData, value: string) => {
        if (field === "postalCode") return /^[0-9]+$/.test(value);
        if (field === "telephoneCountryCode") return /^\+?[0-9]+$/.test(value);
        if (field === "telephoneNumber") return /^[0-9 /-]+$/.test(value);
        return value !== "";
    };

    // checkin action
    const checkinAction = async () => {
        const mainContact = { fullName, street, postalCode, city, telephoneCountryCode, telephoneNumber };
        const completedCompanionsContactData = companionsContactData.map((data, i) => (notSameContactData.includes(i) ? data : { ...mainContact, fullName: data.fullName }));

        // validate all input fields
        const isInvalid = (contact: ContactData) => Object.entries(contact).some(([k, v]) => !isValidValue(k as keyof ContactData, v));
        if (isInvalid(mainContact) || completedCompanionsContactData.some(isInvalid)) {
            setShowInvalidFields(true);
            return;
        }

        setLoading(true);
        try {
            await postCheckIns({
                clientLatitude: parseFloat(urlData.latitude),
                clientLongitude: parseFloat(urlData.longitude),
                clubId: urlData.clubId,
                companionsContactData: completedCompanionsContactData,
                contactData: {
                    ...mainContact,
                    consentDate: moment().toISOString(),
                    storePermanently: false,
                },
                durationMins,
                guestIdentifier: getPersistedData(PersistedData.GuestIdentifier)!,
            });
            fetchCurrentCheckin();
            setShowCheckedOutSuccess(false);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // checkout action
    const checkoutAction = async () => {
        setLoading(true);
        try {
            await patchCheckInsEnd({
                checkInId: checkinInfo!.activeCheckInId!,
                guestIdentifier: getPersistedData(PersistedData.GuestIdentifier)!,
            });
            setShowCheckedOutSuccess(true);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // fetch current checkin if exists
    const fetchCurrentCheckin = async () => {
        // get or create guest identifier
        let guestIdentifier = getPersistedData(PersistedData.GuestIdentifier);
        if (!guestIdentifier) {
            guestIdentifier = uuidv4();
            setPersistedData(PersistedData.GuestIdentifier, guestIdentifier);
        }

        // fetch info
        const { clubId } = urlData;
        setCheckinInfo(await getCheckInsSingleClub({ clubId, guestIdentifier }));
    };
    useEffect(() => { isValidLink && fetchCurrentCheckin(); }, [urlData]);

    // render
    return (
        <Container maxWidth="sm" style={{ marginTop: "32px" }}>
            <Grid container spacing={2}>

                {/* header */}
                <Grid item xs={12} style={{ textAlign: "center" }}>
                    <img src="images/playsports_logo.svg" alt="PLAYSPORTS" style={{ height: "96px", margin: "32px" }} />
                    <Typography variant="h6">{isValidLink ? i18n.checkIn : i18n.invalidLink}</Typography>
                </Grid>

                {/* content */}
                {isValidLink && (checkinInfo?.activeCheckInId ? (
                    <>
                        {/* currently checked-in */}
                        <Grid item xs={12}>
                            <Typography align="center" variant="body2">
                                {showCheckedOutSuccess
                                    ? i18n.checkOutSuccessful
                                    : i18n.activeCheckIn(checkinInfo.club.clubName, moment(checkinInfo.userIsCheckedInUntilDate).format("H:mm"))}
                            </Typography>
                        </Grid>
                        {!showCheckedOutSuccess && (
                            <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                                <Button
                                    disabled={isLoading}
                                    style={{ background: "#d44336", color: "#fff" }}
                                    variant="contained"
                                    onClick={checkoutAction}
                                >
                                    {i18n.checkOut}
                                </Button>
                            </Grid>
                        )}
                    </>
                ) : (
                    <>
                        {/* currently checked-out */}
                        <GridTextField xsSize={12} size={6} label={i18n.club} value={checkinInfo?.club.clubName || ""} disabled />
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel>{i18n.duration}</InputLabel>
                                <Select
                                    label={i18n.duration}
                                    value={durationMins}
                                    onChange={(e) => setDurationMins(parseInt(e.target.value as string, 10))}
                                >
                                    {[30, 60, 90, 120, 360, 720].map((d) => (
                                        <MenuItem key={d} value={d}>{i18n.hours(d / 60)}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <GridTextField xsSize={12} size={6} label={i18n.fullName} value={fullName} error={showInvalidFields && !isValidValue("fullName", fullName)} onChange={setFullName} />
                        <GridTextField xsSize={12} size={6} label={i18n.street} value={street} error={showInvalidFields && !isValidValue("street", street)} onChange={setStreet} />
                        <GridTextField xsSize={12} size={6} label={i18n.postalCode} value={postalCode} error={showInvalidFields && !isValidValue("postalCode", postalCode)} onChange={setPostalCode} />
                        <GridTextField xsSize={12} size={6} label={i18n.city} value={city} error={showInvalidFields && !isValidValue("city", city)} onChange={setCity} />
                        <GridTextField xsSize={12} size={6} label={i18n.telephoneCountryCode} value={telephoneCountryCode} error={showInvalidFields && !isValidValue("telephoneCountryCode", telephoneCountryCode)} onChange={setTelephoneCountryCode} />
                        <GridTextField xsSize={12} size={6} label={i18n.telephoneNumber} value={telephoneNumber} error={showInvalidFields && !isValidValue("telephoneNumber", telephoneNumber)} onChange={setTelephoneNumber} />

                        {/* companions */}
                        {companionsContactData.map((c, i) => (
                            // eslint-disable-next-line
                            <React.Fragment key={i}>
                                <Grid item xs={12} style={{ marginTop: "16px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                                    <Typography variant="subtitle2">{`${i18n.person} ${i + 2}`}</Typography>
                                    <Button size="small" startIcon={<CloseIcon />} onClick={() => setCompanionsContactData(companionsContactData.filter((_, di) => i !== di))}>{i18n.remove}</Button>
                                </Grid>
                                {(notSameContactData.includes(i) ? ["fullName", "street", "postalCode", "city", "telephoneCountryCode", "telephoneNumber"] : ["fullName"]).map((p) => (
                                    <GridTextField
                                        error={showInvalidFields && !isValidValue(p as keyof ContactData, c[p as keyof ContactData])}
                                        key={p}
                                        label={i18n[p as keyof ContactData]}
                                        size={6}
                                        value={c[p as keyof ContactData]}
                                        xsSize={12}
                                        onChange={(v) => setCompanionsContactData(Object.assign([], companionsContactData, { [i]: { ...companionsContactData[i], [p as keyof ContactData]: v } }))}
                                    />
                                ))}
                                <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={(
                                                <Switch
                                                    checked={!notSameContactData.includes(i)}
                                                    color="primary"
                                                    onChange={(e) => setNotSameContactData([...notSameContactData.filter((ii) => ii !== i)].concat(e.target.checked ? [] : [i]))}
                                                />
                                            )}
                                            label={i18n.sameContactData}
                                        />
                                    </FormControl>
                                </Grid>
                            </React.Fragment>
                        ))}
                        <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                            <Button startIcon={<AddIcon />} onClick={() => setCompanionsContactData([...companionsContactData, { fullName: "", street: "", postalCode: "", city: "", telephoneCountryCode: "+49", telephoneNumber: "" }])}>
                                {i18n.addPerson}
                            </Button>
                        </Grid>

                        {/* consent and go */}
                        <Grid item xs={12}>
                            <Typography align="center" variant="body2">
                                {i18n.consent}
                                (
                                <Link href="https://playsports.world/privacy/" target="_blank">
                                    {i18n.privacyPolicy}
                                </Link>
                                ).
                            </Typography>
                        </Grid>
                        <Grid item xs={12} style={{ display: "flex", justifyContent: "center", marginBottom: "32px" }}>
                            <Button disabled={isLoading} color="primary" variant="contained" onClick={checkinAction}>
                                {i18n.checkIn}
                            </Button>
                        </Grid>

                        {/* download app notice on android */}
                        {/(android)/i.test(navigator.userAgent) && (
                            <Grid item xs={12}>
                                <Typography align="center" variant="body2" style={{ marginBottom: "32px" }}>
                                    <Link href="https://playsports.world/download/" target="_blank">
                                        {i18n.appDownload}
                                    </Link>
                                </Typography>
                            </Grid>
                        )}
                    </>
                ))}
            </Grid>
        </Container>
    );
};

export default GuestCheckinView;
